import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { expValEquals } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';

import type {
	useLicensedProductsQueryForGlobalAppShortcutsQuery$data,
	useLicensedProductsQueryForGlobalAppShortcutsQuery$variables,
} from './__generated__/useLicensedProductsQueryForGlobalAppShortcutsQuery.graphql.ts';

const useLicensedProductsQueryForGlobalAppShortcuts = graphql`
	query useLicensedProductsQueryForGlobalAppShortcutsQuery {
		confluence_tenantContext {
			licensedProducts {
				licenseStatus
				productKey
			}
		}
	}
`;

export const useLicensedProducts = () => {
	const queryResult = expValEquals('confluence_frontend_stop_watching_queries', 'isEnabled', true)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<
				useLicensedProductsQueryForGlobalAppShortcutsQuery$data,
				useLicensedProductsQueryForGlobalAppShortcutsQuery$variables
			>(useLicensedProductsQueryForGlobalAppShortcuts)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryInterop<
				useLicensedProductsQueryForGlobalAppShortcutsQuery$data,
				useLicensedProductsQueryForGlobalAppShortcutsQuery$variables
			>(useLicensedProductsQueryForGlobalAppShortcuts);

	const { data } = queryResult;

	return useMemo(() => {
		const licensedProducts = data?.confluence_tenantContext?.licensedProducts || [];
		return licensedProducts.reduce(
			(acc, product) => {
				if (product.licenseStatus !== 'ACTIVE') {
					return acc;
				}

				switch (product.productKey) {
					case 'jira-software':
						acc.hasJira = true;
						break;
					case 'loom':
						acc.hasLoom = true;
						break;
					default:
						break;
				}
				return acc;
			},
			{ hasJira: false, hasLoom: false },
		);
	}, [data]);
};
